/**
 * src/components/spatial/eventSummaryModel.ts
 *
 * The questions a person actually asks about an event, answered from
 * what the server already said.
 *
 * What is this, where is it, when is it, is it on now, is anything
 * live, how many people are here, what can I do, can I watch, what
 * sessions are inside it. Ten questions, and every one of them is
 * already answered somewhere on the server: `eventModel` normalizes
 * the event, `eventBroadcastLink` reports the three states apart from
 * each other, `eventPresence` counts, `get_nearby_actions` describes
 * what can be done, `eventBroadcastArchive` says whether there is
 * anything to play back.
 *
 * So this file formats. It is deliberately not allowed to work any of
 * them out for itself, and that restraint is the point: the moment the
 * panel computes "is it live" from a start time, the panel and the AI
 * answer the same question differently, and one of them is wrong in
 * front of somebody.
 *
 * The one thing it does decide is what to say when an answer is
 * missing, which is a presentation question. A count that has not
 * arrived is not zero, a state that is null is not "ended", and both
 * have to read as unknown rather than as a fact.
 */

import type {
  EventBroadcastRelation, EventDetail, EventLive, EventPresenceCounts, EventRecord,
} from '../../services/spatial/spatialEventsClient';

/** An action as the server described it. Mirrors the Phase 5F descriptor. */
export interface EventActionDescriptor {
  id?: string;
  kind?: string;
  verb?: string;
  label?: string;
  readOnly?: boolean;
  requiresSigning?: boolean;
  requiresTier?: string;
  cost?: { known?: boolean; detail?: string } | null;
  executionRuleId?: string | number | null;
  eligibility?: { passing?: boolean; reason?: string | null } | null;
  conditions?: {
    spatial?: { radiusMeters?: number | null; minDwellSeconds?: number | null } | null;
    temporal?: {
      startsAt?: string | null; endsAt?: string | null;
      status?: string | null; state?: string | null;
    } | null;
  } | null;
  contract?: {
    name?: string | null; address?: string | null; network?: string | null;
    functionName?: string | null;
    parameters?: { known?: boolean; detail?: string } | null;
  } | null;
  broadcast?: { available?: boolean } | null;
}

/** A finished broadcast, as the archive publishes it. */
export interface EventArchivedBroadcast {
  broadcastId: string;
  title?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
  durationSec?: number | null;
  archive: { available: boolean; reason?: string | null; recordingId?: string | null };
}

/** A child event, as discovery publishes it. */
export interface EventChild {
  objectId: string;
  name?: string | null;
  event?: EventRecord | null;
  state?: string | null;
  live?: EventLive | null;
}

export type Answer<T> =
  | { known: true; value: T }
  | { known: false; reason: 'not_loaded' | 'not_available' };

const UNKNOWN = Object.freeze({ known: false as const, reason: 'not_loaded' as const });

function known<T>(value: T): Answer<T> { return { known: true, value }; }

export interface EventSummary {
  /** What is this? */
  title: string;
  description: string | null;
  tags: string[];
  organizer: string | null;
  organizerUrl: string | null;
  image: string | null;

  /** Where is it? */
  venue: string | null;
  floorRef: string | null;
  zoneRef: string | null;
  hostObjectId: string | null;

  /** When is it? */
  startsAt: string | null;
  endsAt: string | null;
  timezone: string | null;

  /**
   * Is it happening now?
   *
   * Three separate readings, kept separate. `lifecycle` is what the
   * operator intends, `state` is what the clock says, `happeningNow`
   * is the one-word answer a person wanted — and it is the server's
   * state, not a comparison done here.
   */
  lifecycle: string | null;
  state: string | null;
  happeningNow: Answer<boolean>;

  /** Is anything live? Not the same question as the one above. */
  live: EventLive | null;
  isBroadcasting: Answer<boolean>;
  liveSource: 'event' | 'host' | null;

  /** How many people are present, when that is known? */
  presentCount: Answer<number>;

  /** Can I watch? */
  watch: {
    liveNow: boolean;
    /** Recordings that exist and can be played. */
    playable: EventArchivedBroadcast[];
    /** Broadcasts that happened and were not recorded. */
    unrecorded: EventArchivedBroadcast[];
  };

  /** What can I do here, and what is available to me? */
  actions: {
    available: EventActionDescriptor[];
    blocked: EventActionDescriptor[];
  };

  /** What child events or sessions exist? */
  children: EventChild[];

  /** What the event's data implies about a broadcast. Reported only. */
  relations: EventBroadcastRelation[];

  /** Whether the event is over, for a UI deciding what to offer. */
  concluded: boolean;
}

/**
 * Statuses an event does not come back from.
 *
 * Duplicated from the server's `TERMINAL_STATUSES` for the same reason
 * the verb vocabulary is: the client cannot import server code, and a
 * shared package for one list was explicitly ruled out. Kept honest by
 * a test that compares the two.
 */
export const CONCLUDED_STATUSES: readonly string[] = ['completed', 'cancelled', 'archived'];

function _tags(event: EventRecord | null | undefined): string[] {
  const raw = event?.tags;
  if (!Array.isArray(raw)) return [];
  return raw.filter((t): t is string => typeof t === 'string' && !!t.trim());
}

/**
 * Sort what can be done above what cannot.
 *
 * Within each group the server's order is kept. It ranked by distance
 * and eligibility already, and re-sorting here would quietly override
 * a decision made with more information.
 */
function _splitActions(actions: readonly EventActionDescriptor[]): {
  available: EventActionDescriptor[]; blocked: EventActionDescriptor[];
} {
  const available: EventActionDescriptor[] = [];
  const blocked: EventActionDescriptor[] = [];
  for (const action of actions) {
    if (action?.eligibility?.passing) available.push(action);
    else blocked.push(action);
  }
  return { available, blocked };
}

/**
 * Everything a panel needs, assembled from answers it did not compute.
 *
 * Every input is optional and missing inputs produce unknowns rather
 * than defaults. A panel that renders before its presence request
 * lands should say nothing about attendance, not say nobody came.
 */
export function buildEventSummary(input: {
  detail: EventDetail | null | undefined;
  presence?: EventPresenceCounts | null;
  actions?: readonly EventActionDescriptor[] | null;
  broadcasts?: readonly EventArchivedBroadcast[] | null;
  children?: readonly EventChild[] | null;
}): EventSummary | null {
  const detail = input.detail;
  if (!detail) return null;

  const event = detail.event || {};
  const live = detail.live || null;

  // The server's own reading. A null state is "does not present as an
  // occasion right now", which is what a cancelled event returns
  // whatever its timestamps say — so this cannot be derived from the
  // dates without losing the operator's intent.
  const happeningNow: Answer<boolean> = detail.state === undefined
    ? UNKNOWN
    : known(detail.state === 'live');

  const isBroadcasting: Answer<boolean> = detail.live === undefined
    ? UNKNOWN
    : known(!!live && live.status === 'LIVE');

  const presentCount: Answer<number> = Number.isInteger(input.presence?.eventCount)
    ? known(input.presence!.eventCount)
    : UNKNOWN;

  const broadcasts = Array.isArray(input.broadcasts) ? input.broadcasts : [];
  const actions = Array.isArray(input.actions) ? input.actions : [];

  return {
    title: event.title?.trim() || detail.eventId,
    description: event.description?.trim() || null,
    tags: _tags(event),
    organizer: event.organizer?.name?.trim() || null,
    organizerUrl: event.organizer?.url || null,
    image: event.image || null,

    venue: event.venue?.trim() || null,
    floorRef: event.floorRef || null,
    zoneRef: event.zoneRef || null,
    hostObjectId: detail.hostObjectId || null,

    startsAt: event.startsAt || null,
    endsAt: event.endsAt || null,
    timezone: event.timezone || null,

    lifecycle: detail.lifecycle ?? null,
    state: detail.state ?? null,
    happeningNow,

    live,
    isBroadcasting,
    liveSource: detail.liveSource ?? null,

    presentCount,

    watch: {
      liveNow: !!live && live.status === 'LIVE',
      playable: broadcasts.filter((b) => b?.archive?.available),
      // Named separately rather than filtered out. A broadcast that
      // happened and was not recorded is worth showing as exactly
      // that; hiding it makes the event look as though it was never
      // on air.
      unrecorded: broadcasts.filter((b) => b && !b.archive?.available),
    },

    actions: _splitActions(actions),
    children: Array.isArray(input.children) ? input.children.slice() : [],
    relations: Array.isArray(detail.broadcastRelations) ? detail.broadcastRelations : [],

    concluded: CONCLUDED_STATUSES.includes(detail.lifecycle || ''),
  };
}

// ── Saying it in words ──────────────────────────────────────────────

/**
 * The one-line answer to "is this on?".
 *
 * Reads the server's state rather than the clock. The wording keeps
 * the three readings distinguishable on purpose: an event can be on
 * with nothing broadcast, and a broadcast can be running on an event
 * that was cancelled, and a person looking at the panel should be
 * able to tell.
 */
export function describeWhen(summary: EventSummary | null): string {
  if (!summary) return '';
  if (!summary.happeningNow.known) return 'Checking…';

  switch (summary.state) {
    case 'live': return 'Happening now';
    case 'soon': return 'Starting soon';
    case 'later_today': return 'Later today';
    case 'upcoming': return 'Upcoming';
    case 'recently_ended': return 'Just ended';
    default: break;
  }

  // No state, so the operator's intent is the only thing left to
  // report — and reporting it is better than saying nothing, because
  // "cancelled" is the answer somebody standing outside needs.
  switch (summary.lifecycle) {
    case 'cancelled': return 'Cancelled';
    case 'completed': return 'Finished';
    case 'archived': return 'Archived';
    case 'paused': return 'Paused';
    case 'draft': return 'Not published';
    default: return 'Not scheduled';
  }
}

/** What to say about attendance, including when it is not known. */
export function describePresence(summary: EventSummary | null): string | null {
  if (!summary) return null;
  if (!summary.presentCount.known) return null;
  const count = summary.presentCount.value;
  if (count === 0) return 'Nobody checked in yet';
  return count === 1 ? '1 person here' : `${count} people here`;
}

/**
 * What to say about watching.
 *
 * The unrecorded case gets its own sentence because it is the one the
 * archive work exists to make sayable.
 */
export function describeWatch(summary: EventSummary | null): string | null {
  if (!summary) return null;
  if (summary.watch.liveNow) return 'Live now';
  if (summary.watch.playable.length > 0) {
    return summary.watch.playable.length === 1
      ? '1 recording available'
      : `${summary.watch.playable.length} recordings available`;
  }
  if (summary.watch.unrecorded.length > 0) return 'Ended, archive unavailable';
  return null;
}

/** Whether to offer a watch control at all. */
export function canWatch(summary: EventSummary | null): boolean {
  if (!summary) return false;
  return summary.watch.liveNow || summary.watch.playable.length > 0;
}
